/**
 * JSON API calls (auth, profile and avatar, turn alert subscriptions, feedback, admin tools, game logs and replays, leaderboards). Everything game-related goes over the socket instead.
 * Same-origin only: the session cookie rides along automatically.
 */
import type {
  AdminPlayerDetail,
  AdminPlayerSummary,
  AdminSummary,
  AvatarSubmit,
  FeedbackDetail,
  FeedbackSubmit,
  FeedbackSummary,
  GameLogDetail,
  GameLogSummary,
  Leaderboard,
  LeaderboardFilter,
  Me,
} from '@kadice/shared';

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok) throw new ApiError(res.status, data.error ?? `Request failed (${res.status})`);
  return data;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export const api = {
  /** `pushKey`: the server's Web Push key, null when turn alerts can't be pushed. */
  config: () => call<{ googleClientId: string; devLogin: boolean; pushKey: string | null }>('GET', '/config'),
  /** The signed-in player, or null when there is no session. */
  me: () =>
    call<Me>('GET', '/me').catch((err) => (err instanceof ApiError && err.status === 401 ? null : Promise.reject(err))),
  signIn: (credential: string) => call<Me>('POST', '/auth/google', { credential }),
  /** DEV_LOGIN test mode only: a name plus the shared password. */
  devSignIn: (name: string, password: string) => call<Me>('POST', '/auth/dev', { name, password }),
  signOut: () => call<void>('POST', '/auth/logout'),
  rename: (displayName: string) => call<Me>('PATCH', '/me', { displayName }),
  /** The first-login answer to 'Have you played Ka'Dice before?'. */
  setPlayedBefore: (playedBefore: boolean) => call<void>('PUT', '/me/played-before', { playedBefore }),
  /** A built-in default (1..AVATAR_DEFAULTS) or a square image as a data URL. */
  setAvatar: (avatar: AvatarSubmit) => call<void>('PUT', '/me/avatar', avatar),
  /** Turn alerts: save this browser's push subscription for the signed-in player, or drop it. */
  savePush: (subscription: PushSubscriptionJSON) => call<void>('POST', '/push', subscription),
  deletePush: (endpoint: string) => call<void>('DELETE', '/push', { endpoint }),
  /** Every metric's top players and where you stand (your stat sheet). */
  /** `filter`: only the counted games it matches (each option left out means any). */
  leaderboard: (filter: LeaderboardFilter = {}) => {
    const q = new URLSearchParams(Object.entries(filter).filter((e): e is [string, string] => !!e[1])).toString();
    return call<Leaderboard>('GET', `/leaderboard${q ? `?${q}` : ''}`);
  },
  /** Your most recent counted games, newest first (replays and recaps). */
  recentGames: () => call<GameLogSummary[]>('GET', '/games/recent'),
  /** A finished game's log, for its replay and recap (any signed-in player). */
  gameLog: (id: number) => call<GameLogDetail>('GET', `/games/${id}/log`),
  sendFeedback: (report: FeedbackSubmit) => call<{ id: number }>('POST', '/feedback', report),
  /** Admins only (`players.is_admin`). */
  adminSummary: () => call<AdminSummary>('GET', '/admin/summary'),
  listFeedback: () => call<FeedbackSummary[]>('GET', '/admin/feedback'),
  getFeedback: (id: number) => call<FeedbackDetail>('GET', `/admin/feedback/${id}`),
  listPlayers: (q: string) =>
    call<AdminPlayerSummary[]>('GET', `/admin/players${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  getPlayer: (id: number) => call<AdminPlayerDetail>('GET', `/admin/players/${id}`),
  listGameLogs: () => call<GameLogSummary[]>('GET', '/admin/games'),
  getGameLog: (id: number) => call<GameLogDetail>('GET', `/admin/games/${id}`),
};

export const feedbackImageUrl = (id: number, imageId: number) => `/api/admin/feedback/${id}/images/${imageId}`;
